/**
 * ChartView Component
 * Renders line chart using react-chartjs-2 for tracking data visualization
 *
 * Requirements:
 * - 2.3: Update graph when field selection changes
 * - 3.1: Render chart in upper 50% of available vertical space
 * - 4.1: Highlight corresponding data point when track point is selected
 * - 4.2: Remove highlight when track point is deselected
 */

import React, { useMemo, useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { CHART_CONFIG } from './constants';
import 'chartjs-adapter-moment';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * ChartView Component
 *
 * @param {Object} props
 * @param {Object} props.data - Chart.js compatible data object with labels and datasets
 * @param {Array} props.selectedFields - Array of selected field keys to display
 * @param {number} props.highlightedIndex - Index of highlighted point (-1 if none)
 * @param {Array} props.trackPoints - Original track points array for reference
 * @param {Function} props.onPointClick - Optional callback when a chart point is clicked
 */
export const ChartView = ({
  data,
  selectedFields = [],
  highlightedIndex = -1,
  trackPoints = [],
  onPointClick,
}) => {
  const chartRef = useRef(null);

  // Scroll chart to show highlighted point when it changes
  useEffect(() => {
    if (chartRef.current && highlightedIndex >= 0 && data?.labels?.length > 0) {
      const chart = chartRef.current;
      // Chart.js will automatically handle the point visibility
      // The highlight styling makes the point visible
      chart.update('none');
    }
  }, [highlightedIndex, data]);

  // Chart options configuration with time-based x-axis
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: false, // Hide default legend, we'll use custom legend on the right
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleFont: {
            size: 13,
          },
          bodyFont: {
            size: 12,
          },
          padding: 10,
          callbacks: {
            title: (context) => {
              if (context[0]) {
                return `Time: ${context[0].label}`;
              }
              return '';
            },
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${value != null ? value.toFixed(2) : 'N/A'}`;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Source Time',
            font: {
              size: 12,
              weight: 'bold',
            },
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 20,
            font: {
              size: 10,
            },
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Value',
            font: {
              size: 12,
              weight: 'bold',
            },
          },
          beginAtZero: false,
          ticks: {
            font: {
              size: 10,
            },
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
      elements: {
        point: {
          radius: CHART_CONFIG.pointRadius,
          hoverRadius: CHART_CONFIG.pointHoverRadius,
        },
        line: {
          tension: CHART_CONFIG.tension,
        },
      },
      onClick: (_event, elements) => {
        if (elements.length > 0 && onPointClick && trackPoints.length > 0) {
          const index = elements[0].index;
          if (index >= 0 && index < trackPoints.length) {
            onPointClick(trackPoints[index]);
          }
        }
      },
    }),
    [onPointClick, trackPoints]
  );

  // Add highlight styling to data if a point is selected
  // Per Requirements 4.1: Highlight corresponding data point when track point is selected
  // Per Requirements 4.2: Remove highlight when track point is deselected
  const chartDataWithHighlight = useMemo(() => {
    if (!data || !data.datasets || data.datasets.length === 0) return data;

    // If no point is highlighted, return data as-is
    if (highlightedIndex < 0) {
      return data;
    }

    // Clone datasets and add highlight styling for the selected point
    const highlightedDatasets = data.datasets.map((dataset) => {
      const dataLength = data.labels?.length || 0;
      const pointRadius = new Array(dataLength).fill(CHART_CONFIG.pointRadius);
      const pointBorderWidth = new Array(dataLength).fill(1);
      const pointBorderColor = new Array(dataLength).fill(dataset.borderColor);
      const pointBackgroundColor = new Array(dataLength).fill(dataset.borderColor);

      // Apply highlight styling to the selected point
      if (highlightedIndex >= 0 && highlightedIndex < dataLength) {
        pointRadius[highlightedIndex] = CHART_CONFIG.highlightPointRadius;
        pointBorderWidth[highlightedIndex] = CHART_CONFIG.highlightPointBorderWidth;
        pointBorderColor[highlightedIndex] = CHART_CONFIG.highlightPointBorderColor;
        pointBackgroundColor[highlightedIndex] = dataset.borderColor;
      }

      return {
        ...dataset,
        pointRadius,
        pointBorderWidth,
        pointBorderColor,
        pointBackgroundColor,
      };
    });

    return {
      ...data,
      datasets: highlightedDatasets,
    };
  }, [data, highlightedIndex]);

  // Empty state - no data or no selected fields
  if (!data || !data.datasets || data.datasets.length === 0) {
    return (
      <div className="chart-view chart-view--empty">
        <p>{selectedFields.length === 0 ? 'Select fields to display on the chart' : 'No data available for selected fields'}</p>
      </div>
    );
  }

  return (
    <div className="chart-view">
      <div className="chart-view__chart-wrapper">
        <Line
          ref={chartRef}
          data={chartDataWithHighlight}
          options={options}
          redraw={false}
        />
      </div>
      <div className="chart-view__legend">
        {data.datasets.map((dataset, index) => (
          <div key={index} className="chart-view__legend-item">
            <span
              className="chart-view__legend-color"
              style={{ backgroundColor: dataset.borderColor }}
            />
            <span className="chart-view__legend-label">
              {dataset.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChartView;
